import type { TFunction } from "i18next";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { Api } from "services/api/api.service";
import type {
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
} from "services/api/company/staff.service";
import type { AddStaffFormData } from "./addStaffPanel.contract";
import {
  applyEmploymentTemplateToAddStaffForm,
  mapAddStaffFormDataToWorkerPayload,
} from "./addStaffPanel.mapper";
import { resolveCreateWorkerErrorMessage } from "./addStaffPanel.error.logic";

export type PhoneLookupState =
  | { readonly status: "idle" }
  | { readonly status: "checking" }
  | { readonly message: string; readonly status: "invalid" }
  | { readonly message: string; readonly status: "alreadyExists" }
  | { readonly status: "verified" };

export type SubmitState =
  | { readonly status: "idle" }
  | { readonly status: "submitting" };

const MAX_PHONE_DIGITS = 15;

interface FetchEmployeeNumberDeps {
  readonly companyId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly requestId: number;
  readonly getCurrentRequestId: () => number;
  readonly setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
  readonly setIsLoadingEmployeeNumber: Dispatch<SetStateAction<boolean>>;
}

export async function fetchEmployeeNumberRequest(
  deps: FetchEmployeeNumberDeps,
): Promise<void> {
  try {
    const response =
      await Api().company.organizationalStructure.getEmployeeNumber(
        deps.companyId,
      );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setField("employeeNumber", String(response.data ?? ""));
  } catch {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setIsLoadingEmployeeNumber(false);
  }
}

interface FetchEmploymentTemplatesDeps {
  readonly companyId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly requestId: number;
  readonly getCurrentRequestId: () => number;
  readonly setEmploymentTemplates: Dispatch<
    SetStateAction<EmploymentTemplateListItem[]>
  >;
  readonly setIsLoadingEmploymentTemplates: Dispatch<SetStateAction<boolean>>;
}

export async function fetchEmploymentTemplatesRequest(
  deps: FetchEmploymentTemplatesDeps,
): Promise<void> {
  try {
    const response = await Api().company.staff.getEmploymentTemplates(
      deps.companyId,
    );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setEmploymentTemplates(response.data ?? []);
  } catch {
    if (!deps.isMountedRef.current) return;
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setIsLoadingEmploymentTemplates(false);
  }
}

interface FetchEmploymentTemplateDetailDeps {
  readonly companyId: number;
  readonly employmentTemplateId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly requestId: number;
  readonly getCurrentRequestId: () => number;
  readonly setFormData: Dispatch<SetStateAction<AddStaffFormData>>;
  readonly setTemplateSalaries: Dispatch<
    SetStateAction<EmploymentTemplateSalary[]>
  >;
}

export async function fetchEmploymentTemplateDetailRequest(
  deps: FetchEmploymentTemplateDetailDeps,
): Promise<void> {
  try {
    const response = await Api().company.staff.getEmploymentTemplate(
      deps.companyId,
      deps.employmentTemplateId,
    );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    const template = response.data;
    if (template == null) return;
    deps.setFormData((previousFormData) =>
      applyEmploymentTemplateToAddStaffForm(previousFormData, template),
    );
    deps.setTemplateSalaries(template.salaries ?? []);
  } catch {
    if (!deps.isMountedRef.current) return;
  }
}

interface LookupWorkerByPhoneDeps {
  readonly companyId: number;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly phoneNumber: string;
  readonly requestId: number;
  readonly translate: TFunction;
  readonly getCurrentRequestId: () => number;
  readonly setFormData: Dispatch<SetStateAction<AddStaffFormData>>;
  readonly setPhoneLookupState: Dispatch<SetStateAction<PhoneLookupState>>;
}

export async function lookupWorkerByPhoneRequest(
  deps: LookupWorkerByPhoneDeps,
): Promise<void> {
  try {
    const response =
      await Api().company.organizationalStructure.getWorkerByNumber(
        deps.companyId,
        deps.phoneNumber,
      );
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;

    const worker = response.data;
    if (!worker || worker.workerId === 0) {
      deps.setPhoneLookupState({ status: "verified" });
      return;
    }
    if (worker.employmentInfo != null) {
      deps.setPhoneLookupState({
        status: "alreadyExists",
        message: deps.translate("Staff.AddStaffPanelWorkerAlreadyExists"),
      });
      return;
    }

    deps.setPhoneLookupState({ status: "verified" });
    deps.setFormData((previousFormData) => ({
      ...previousFormData,
      firstName: worker.firstName ?? "",
      lastName: worker.lastName ?? "",
      email: worker.email ?? "",
      personalIdentityNumber: worker.personalIdentityNumber ?? "",
      phoneNumber: (worker.phoneNumber ?? previousFormData.phoneNumber)
        .replace(/\D/g, "")
        .slice(0, MAX_PHONE_DIGITS),
    }));
  } catch {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
  } finally {
    if (!deps.isMountedRef.current) return;
    deps.setPhoneLookupState((previousPhoneLookupState) => {
      return previousPhoneLookupState.status === "checking"
        ? { status: "idle" }
        : previousPhoneLookupState;
    });
  }
}

interface CreateWorkerDeps {
  readonly companyId: number;
  readonly formData: AddStaffFormData;
  readonly isMountedRef: MutableRefObject<boolean>;
  readonly payload: ReturnType<typeof mapAddStaffFormDataToWorkerPayload>;
  readonly requestId: number;
  readonly translate: TFunction;
  readonly getCurrentRequestId: () => number;
  readonly onClose: () => void;
  readonly onWorkerCreated?: () => void;
  readonly setSubmitError: Dispatch<SetStateAction<string>>;
  readonly setSubmitState: Dispatch<SetStateAction<SubmitState>>;
}

export async function createWorkerRequest(
  deps: CreateWorkerDeps,
): Promise<void> {
  try {
    await Api().company.staff.createWorker(deps.companyId, deps.payload);
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.onWorkerCreated?.();
    deps.onClose();
  } catch (error) {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setSubmitError(
      resolveCreateWorkerErrorMessage({
        error,
        formData: deps.formData,
        translate: deps.translate,
      }),
    );
  } finally {
    if (!deps.isMountedRef.current) return;
    if (deps.getCurrentRequestId() !== deps.requestId) return;
    deps.setSubmitState({ status: "idle" });
  }
}
